import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import LaunchIcon from "@mui/icons-material/Launch";
import EmailIcon from "@mui/icons-material/Email";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import topbarImage from "../assets/Windroos-topbar.png";
import { getLegacySiteUrl } from "../config/migrationStatus";
import { COLORS } from "../theme/tokens";

function TopRibbon() {
  return (
    <Box
      aria-hidden="true"
      sx={{
        backgroundColor: COLORS.lightBlue,
        borderBottom: `1px solid ${COLORS.line}`,
        overflow: "hidden",
      }}
    >
      <Box
        component="img"
        src={topbarImage}
        alt=""
        sx={{
          display: "block",
          width: "100%",
          maxHeight: 104,
          objectFit: "cover",
          objectPosition: "center",
        }}
      />
    </Box>
  );
}

function BrandBar() {
  return (
    <Box
      component="header"
      sx={{
        backgroundColor: "rgba(255, 255, 255, 0.94)",
        borderBottom: `1px solid ${COLORS.line}`,
        px: "clamp(18px, 4vw, 56px)",
        py: 1.3,
      }}
    >
      <Box
        sx={{
          maxWidth: 1160,
          mx: "auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box
          component="a"
          href="/"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1.25,
            color: COLORS.red,
            textDecoration: "none",
            fontFamily: "windroos, Georgia, serif",
            fontSize: "clamp(1.7rem, 3vw, 2.25rem)",
            lineHeight: 1,
          }}
          aria-label="De Windroos"
        >
          <Box
            component="span"
            aria-hidden="true"
            sx={{
              width: 38,
              height: 38,
              borderRadius: "50%",
              backgroundColor: COLORS.orange,
              color: "#fff",
              display: "inline-grid",
              placeItems: "center",
              fontFamily: "windroos, Georgia, serif",
              fontSize: "1rem",
              boxShadow: "0 8px 20px rgba(239, 125, 0, 0.18)",
            }}
          >
            W
          </Box>
          Windroos
        </Box>
        <Typography sx={{ color: COLORS.muted, fontWeight: 700 }}>
          Websitevernieuwing
        </Typography>
      </Box>
    </Box>
  );
}

export function MigrationHoldingPage() {
  const legacySiteUrl = getLegacySiteUrl();

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: COLORS.paper, color: COLORS.ink }}>
      <TopRibbon />
      <BrandBar />
      <Box
        component="main"
        sx={{
          px: "clamp(20px, 5vw, 72px)",
          py: "clamp(52px, 8vw, 94px)",
          minHeight: "70vh",
          display: "grid",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            maxWidth: 1160,
            mx: "auto",
            width: "100%",
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.06fr 0.94fr" },
            gap: { xs: 4, md: 7 },
            alignItems: "center",
          }}
        >
          <Stack spacing={2.25}>
            <Typography
              sx={{
                color: COLORS.red,
                fontWeight: 900,
                letterSpacing: "0.07em",
                textTransform: "uppercase",
                fontSize: "0.78rem",
              }}
            >
              We gaan bijna live
            </Typography>
            <Typography
              variant="h1"
              sx={{
                fontFamily: "windroos, Georgia, serif",
                color: COLORS.blue,
                fontSize: "clamp(2.7rem, 6vw, 5.8rem)",
                lineHeight: 1,
                m: 0,
                p: 0,
              }}
            >
              De Windroos krijgt een nieuwe website
            </Typography>
            <Typography sx={{ color: COLORS.ink, fontSize: "1.12rem", lineHeight: 1.7, maxWidth: 690 }}>
              We zetten op dit moment de nieuwe website klaar. Daardoor kan de
              site tijdelijk minder goed bereikbaar zijn of er even anders
              uitzien dan je gewend bent.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
              {legacySiteUrl && (
                <Button
                  href={legacySiteUrl}
                  startIcon={<LaunchIcon />}
                  disableElevation
                  sx={{
                    alignSelf: "flex-start",
                    borderRadius: 1,
                    minHeight: 46,
                    px: 2.4,
                    color: "#fff",
                    backgroundColor: COLORS.orange,
                    textTransform: "none",
                    fontWeight: 900,
                    "&:hover": { backgroundColor: COLORS.red },
                  }}
                >
                  Naar de oude website
                </Button>
              )}
              <Button
                href="mailto:vernieuwingsschool@windroos.nl"
                startIcon={<EmailIcon />}
                variant="outlined"
                sx={{
                  alignSelf: "flex-start",
                  borderRadius: 1,
                  minHeight: 46,
                  px: 2.4,
                  borderColor: COLORS.blue,
                  color: COLORS.blue,
                  textTransform: "none",
                  fontWeight: 900,
                }}
              >
                Neem contact op
              </Button>
            </Stack>
          </Stack>

          <Box
            sx={{
              backgroundColor: "#fff",
              border: `1px solid ${COLORS.line}`,
              borderRadius: 1,
              p: { xs: 2.25, md: 3 },
              boxShadow: "0 16px 36px rgba(32, 48, 51, 0.08)",
            }}
          >
            <Typography
              sx={{
                color: COLORS.red,
                fontWeight: 900,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                fontSize: "0.76rem",
                mb: 1.5,
              }}
            >
              Goed om te weten
            </Typography>
            <Stack component="ul" spacing={1.2} sx={{ listStyle: "none", p: 0, m: 0 }}>
              {[
                "De school blijft gewoon bereikbaar.",
                "Tijdens de overgang kan een pagina tijdelijk niet laden.",
                "De oude website blijft nog even beschikbaar via de knop op deze pagina.",
              ].map((item) => (
                <Box
                  component="li"
                  key={item}
                  sx={{ display: "grid", gridTemplateColumns: "24px 1fr", gap: 1, alignItems: "start" }}
                >
                  <InfoOutlinedIcon sx={{ color: COLORS.orange, fontSize: 20, mt: "2px" }} />
                  <Typography sx={{ color: COLORS.ink, lineHeight: 1.6 }}>{item}</Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        </Box>
      </Box>
      <Box
        component="footer"
        sx={{ px: "clamp(20px, 5vw, 72px)", py: 3, borderTop: `1px solid ${COLORS.line}`, backgroundColor: "#fff" }}
      >
        <Typography sx={{ maxWidth: 1160, mx: "auto", color: COLORS.muted, lineHeight: 1.6 }}>
          Mail naar vernieuwingsschool@windroos.nl of bel 0343-592798.
        </Typography>
      </Box>
    </Box>
  );
}
